import React, { useState, useEffect } from 'react';
import { Box, Container, Typography } from '@mui/joy';
import { useLocation, useNavigate } from 'react-router-dom';
import { getEmployerMeetings, getCurrentUser } from '../../../apiconfig/api';
import { useSession } from '../../../context/SessionContext';
import { MeetingsTabs, MeetingsList } from './components';
import MeetingDetailsModal from './components/MeetingDetailsModal';

const getTabFromHash = (hash: string) => {
  if (hash === '#past') return 1;
  return 0; // default to upcoming
};

const MeetingListPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { sessionEmployerId } = useSession();
  const [tab, setTab] = useState(getTabFromHash(location.hash));
  const [meetings, setMeetings] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    setTab(getTabFromHash(location.hash));
  }, [location.hash]);

  useEffect(() => {
    // Get employerId from current user
    const fetchUser = async () => {
      try {
        const userResp = await getCurrentUser();
        // If your user object has employerId, use it. Otherwise, use id.
        // setEmployerId(userResp.data.user.employerId || userResp.data.user.id); // This line is removed
      } catch (err) {
        setError('Failed to load user info.');
      }
    };
    fetchUser();
  }, []);

  const fetchMeetings = async (pageNum = 1, tabValue = tab) => {
    if (!sessionEmployerId) return;
    setLoading(true);
    setError(null);
    try {
      const now = new Date().toISOString();
      const params: any = { page: pageNum, per_page: 10 };
      
      if (tabValue === 0) {
        params.after = now; // future meetings
        params.sort_by = 'scheduledAt';
        params.sort_direction = 'asc'; // soonest first
      } else {
        params.before = now; // past meetings
        params.sort_by = 'scheduledAt';
        params.sort_direction = 'desc'; // most recent first
      }
      
      const resp = await getEmployerMeetings(sessionEmployerId, params);
      setMeetings(pageNum === 1 ? resp.data.meetings : [...meetings, ...resp.data.meetings]);
      setHasMore(resp.data.hasMore || false);
    } catch (err: any) {
      setError(err.message || 'Failed to load meetings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sessionEmployerId) {
      setPage(1);
      fetchMeetings(1, tab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, sessionEmployerId]);

  const handleTabChange = (_event: React.SyntheticEvent | null, value: number | string | null) => {
    if (typeof value === 'number') {
      setTab(value);
      navigate(`#${value === 0 ? 'upcoming' : 'past'}`);
    }
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchMeetings(nextPage, tab);
  };

  const handleMeetingClick = (meeting: any) => {
    setSelectedMeeting(meeting);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedMeeting(null);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f4f4', py: 4 }}>
      <Container maxWidth="lg">
        <Typography level="h2" sx={{ mb: 3, fontWeight: 700 }}>
          Scheduled Meetings
        </Typography>
        
        <MeetingsTabs tab={tab} onTabChange={handleTabChange} />
        
        <MeetingsList
          meetings={meetings}
          loading={loading}
          error={error}
          page={page}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          onMeetingClick={handleMeetingClick}
        />
        
        <MeetingDetailsModal
          open={showModal}
          onClose={handleCloseModal}
          meeting={selectedMeeting}
        />
      </Container>
    </Box>
  );
};

export default MeetingListPage; 